import { PromptType } from '@communities/prompt-management/domain';
import { useCommunityPrompts, useDeleteCommunityPrompt, usePromptDialogs } from '@communities/prompt-management/hooks';
import { parsePromptVersionDate } from '@communities/prompt-management/utils';
import { routes as communityRoutes } from '@communities/routes';
import type { TableColumn } from '@core/components';
import { DataList, SectionHeader, TimestampDisplay } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Badge, BadgeVariant, Button, ButtonType, IconButton, IconButtonSize, IconSize, InlineTabs, Info16, TestBeaker } from '@soteria/design-system-ve';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PromptsMoreInfoDialog } from './PromptsMoreInfoDialog';
import * as styles from './PromptsSection.css';
import { RegeneratePromptDialog } from './RegeneratePromptDialog';
import { ViewPromptDialog } from './ViewPromptDialog';

/** Sort prompt versions with the production version first, then reverse chronological */
function sortVersionsWithProdFirst(versions: string[], prodVersion?: string): string[] {
  return [...versions].sort((a, b) => {
    if (a === prodVersion) return -1;
    if (b === prodVersion) return 1;

    return b.localeCompare(a);
  });
}

interface ActivePromptDataInput {
  isUsernameTab: boolean;
  promptData:
    | {
        availablePromptVersions?: { classification?: string[]; username?: string[] };
        prodPromptVersion?: { classification?: string; username?: string };
      }
    | null
    | undefined;
  classificationVersions: string[];
  usernameVersions: string[];
}

/** Derive the active tab's versions, production version, and prompt type */
function getActivePromptData({ isUsernameTab, promptData, classificationVersions, usernameVersions }: ActivePromptDataInput) {
  const versions = isUsernameTab ? usernameVersions : classificationVersions;
  const prodVersion = isUsernameTab ? promptData?.prodPromptVersion?.username : promptData?.prodPromptVersion?.classification;
  const promptType = isUsernameTab ? PromptType.Username : PromptType.Classification;

  return { isUsernameTab, versions, prodVersion, promptType };
}

/** Compute the total prompt count across all types */
function getTotalPromptCount(
  isUsernameEnabled: boolean,
  promptData: { availablePromptVersions?: { classification?: string[]; moderation?: string[]; username?: string[] } } | null | undefined
): number {
  const { classification = [], moderation = [], username = [] } = promptData?.availablePromptVersions ?? {};

  return classification.length + moderation.length + (isUsernameEnabled ? username.length : 0);
}

interface PromptsSectionProps {
  communityId: string;
  isCommunityAdmin: boolean;
}

/**
 * PromptsSection Component
 *
 * Self-contained section for managing classification and username prompts within a community.
 * Displays prompt versions, allows generation, testing, viewing, and deletion.
 */
// eslint-disable-next-line complexity
function PromptsSectionComponent({ communityId, isCommunityAdmin }: PromptsSectionProps) {
  const navigate = useNavigate();
  const isUsernameEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const { promptData, loading, errors } = useCommunityPrompts(communityId);
  const { deletePrompt, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunityPrompt();

  const [selectedPromptVersion, setSelectedPromptVersion] = useState<string | undefined>(undefined);
  const dialogs = usePromptDialogs();
  const [activeTab, setActiveTab] = useState<PromptType>(PromptType.Classification);

  const classificationVersions = useMemo(
    () => sortVersionsWithProdFirst(promptData?.availablePromptVersions?.classification || [], promptData?.prodPromptVersion?.classification),
    [promptData]
  );

  const usernameVersions = useMemo(
    () => sortVersionsWithProdFirst(promptData?.availablePromptVersions?.username || [], promptData?.prodPromptVersion?.username),
    [promptData]
  );

  const {
    isUsernameTab,
    versions: activeVersions,
    prodVersion: activeProdVersion,
    promptType: activePromptType,
  } = useMemo(
    () => getActivePromptData({ isUsernameTab: activeTab === PromptType.Username, promptData, classificationVersions, usernameVersions }),
    [activeTab, promptData, classificationVersions, usernameVersions]
  );

  const handleTestPrompt = (promptVersion: string) => {
    const testPromptUrl = communityRoutes.communities.classificationTesting.withPrompt(communityId, promptVersion, {
      promptType: activePromptType,
    });

    navigate(testPromptUrl);
  };

  const isProductionVersion = (promptVersion: string) => promptVersion === activeProdVersion;

  const columns: TableColumn<string>[] = [
    {
      key: 'version',
      header: 'Version',
      render: promptVersion => promptVersion,
    },
    {
      key: 'creationDate',
      header: 'Created',
      render: promptVersion => {
        const date = parsePromptVersionDate(promptVersion);

        return date ? <TimestampDisplay timestamp={date} /> : promptVersion;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: promptVersion => {
        const isProdPrompt = isProductionVersion(promptVersion);

        return <Badge text={isProdPrompt ? 'Production' : 'Inactive'} variant={isProdPrompt ? BadgeVariant.Approved : BadgeVariant.Pending} />;
      },
    },
  ];

  const deleteItemContext =
    isCommunityAdmin && communityId
      ? {
          handler: async (item: string) => await deletePrompt(communityId, { promptVersion: item, type: activePromptType }),
          isDeleting,
          errors: deleteErrors,
          resetErrors,
          isDeleteDisabled: (item: string) => isProductionVersion(item),
          getDeleteDisabledReason: (item: string) => (isProductionVersion(item) ? 'Cannot delete the production prompt version' : undefined),
          getItemDisplayName: (item: string) => item,
        }
      : undefined;

  const tabs = useMemo(
    () => [{ label: `Classification Prompts (${classificationVersions.length})` }, { label: `Username Prompts (${usernameVersions.length})` }],
    [classificationVersions.length, usernameVersions.length]
  );

  const handleTabChange = useCallback((tabIndex: number) => {
    setActiveTab(tabIndex === 1 ? PromptType.Username : PromptType.Classification);
    setSelectedPromptVersion(undefined);
  }, []);

  return (
    <>
      <SectionHeader
        description="View and manage safety prompts for content classification."
        endComponent={
          <div className={styles.headerActions}>
            <IconButton
              aria-label="Learn more about safety prompts"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={dialogs.openMoreInfo}
            />
            {isCommunityAdmin && <Button onClick={dialogs.openGenerate}>GENERATE PROMPT</Button>}
          </div>
        }
        title={`Safety Prompts (${getTotalPromptCount(isUsernameEnabled, promptData)})`}
      />

      {isUsernameEnabled && (
        <div className={styles.tabsWrapper}>
          <InlineTabs aria-label="Prompt type tabs" size="medium" tabs={tabs} value={isUsernameTab ? 1 : 0} variant="default" onValueChange={handleTabChange} />
        </div>
      )}

      <DataList
        additionalAction={
          isCommunityAdmin
            ? {
                icon: TestBeaker,
                label: 'Test',
                onClick: handleTestPrompt,
                buttonType: ButtonType.Primary,
              }
            : undefined
        }
        columns={columns}
        data={activeVersions}
        dataTypeName="prompt"
        deleteItemContext={deleteItemContext}
        error={errors}
        isLoading={loading}
        onView={item => {
          setSelectedPromptVersion(item);
          dialogs.openView();
        }}
      />
      {promptData?.lastModifiedAt && promptData?.lastModifiedBy && (
        <div className={styles.lastModifiedInfo}>
          Last modified: <TimestampDisplay timestamp={promptData.lastModifiedAt} />
          {promptData.lastModifiedBy && ` by ${promptData.lastModifiedBy}`}
        </div>
      )}

      {/* View Prompt Dialog */}
      <ViewPromptDialog
        communityId={communityId}
        isCommunityAdmin={isCommunityAdmin}
        isOpen={dialogs.isViewOpen}
        isProductionPrompt={isProductionVersion(selectedPromptVersion || '')}
        promptType={activePromptType}
        promptVersion={selectedPromptVersion || undefined}
        onClose={dialogs.closeView}
      />

      {/* Regenerate Prompt Dialog */}
      <RegeneratePromptDialog communityId={communityId} isOpen={dialogs.isGenerateOpen} onClose={dialogs.closeGenerate} />

      {/* More Info Dialog */}
      <PromptsMoreInfoDialog isOpen={dialogs.isMoreInfoOpen} showUsernameNote={isUsernameEnabled} onClose={dialogs.closeMoreInfo} />
    </>
  );
}

export const PromptsSection = withAppInsightsTracking(PromptsSectionComponent, 'PromptsSection');
